import { Component, ChangeDetectionStrategy, input, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'widget-alan-grafik',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <svg
      [attr.viewBox]="'0 0 ' + genislik() + ' ' + yukseklik()"
      preserveAspectRatio="none"
      class="alan-grafik-svg"
      (mousemove)="fareHareket($event)"
      (mouseleave)="hoverIdx.set(-1)">
      <defs>
        <linearGradient [attr.id]="gradientId" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" [attr.stop-color]="renk()" [attr.stop-opacity]="doldurmaOpakligi()" />
          <stop offset="100%" [attr.stop-color]="renk()" stop-opacity="0.02" />
        </linearGradient>
      </defs>

      @for (y of gridCizgileri(); track y) {
        <line [attr.x1]="dolgu" [attr.y1]="y" [attr.x2]="genislik() - dolgu" [attr.y2]="y"
              stroke="currentColor" stroke-opacity="0.08" stroke-width="0.5" />
      }

      @if (noktalar().length > 1) {
        <polygon [attr.points]="alanNoktalari()" [attr.fill]="'url(#' + gradientId + ')'" />
        <polyline [attr.points]="cizgiNoktalari()" fill="none" [attr.stroke]="renk()"
                  stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round" />

        @if (hoverNokta(); as hn) {
          <line [attr.x1]="hn.x" [attr.y1]="dolgu" [attr.x2]="hn.x" [attr.y2]="yukseklik() - dolgu"
                stroke="currentColor" stroke-opacity="0.15" stroke-width="0.5" stroke-dasharray="2,2" />
          <circle [attr.cx]="hn.x" [attr.cy]="hn.y" r="2.5" [attr.fill]="renk()" stroke="#fff" stroke-width="1" />
        }
      }
    </svg>

    @if (hoverBilgi(); as bilgi) {
      <div class="tooltip" [style.left.px]="bilgi.solPx">
        <span class="deger">{{ bilgi.deger }}</span>
        @if (bilgi.etiket) {
          <span class="etiket">{{ bilgi.etiket }}</span>
        }
      </div>
    }
  `,
  styles: [`
    :host { display: block; position: relative; line-height: 0; }
    .alan-grafik-svg { width: 100%; height: auto; cursor: crosshair; }
    .tooltip {
      position: absolute; top: -4px; transform: translateX(-50%) translateY(-100%);
      background: rgba(0,0,0,.78); color: #fff; padding: 2px 6px; border-radius: 4px;
      font-size: 11px; white-space: nowrap; pointer-events: none;
      display: flex; flex-direction: column; align-items: center; gap: 1px;
    }
    .tooltip .deger { font-weight: 600; }
    .tooltip .etiket { opacity: .7; font-size: 10px; }
  `],
})
export class AlanGrafikComponent {
  veriler = input<number[]>([]);
  etiketler = input<string[]>([]);
  renk = input('#1890ff');
  doldurmaOpakligi = input(0.3);
  genislik = input(200);
  yukseklik = input(80);

  readonly dolgu = 4;
  readonly gridSayisi = 3;
  readonly gradientId = 'ag-grad-' + Math.random().toString(36).slice(2, 8);

  hoverIdx = signal(-1);
  private svgRect: DOMRect | null = null;

  noktalar = computed(() => {
    const v = this.veriler();
    if (!v?.length) return [];
    const g = this.genislik();
    const h = this.yukseklik();
    const p = this.dolgu;
    const minV = Math.min(...v);
    const maxV = Math.max(...v);
    const range = maxV - minV || 1;
    const adim = v.length > 1 ? (g - p * 2) / (v.length - 1) : 0;
    return v.map((d, i) => ({
      x: p + i * adim,
      y: p + (h - p * 2) * (1 - (d - minV) / range),
    }));
  });

  cizgiNoktalari = computed(() =>
    this.noktalar().map(n => `${n.x},${n.y}`).join(' ')
  );

  alanNoktalari = computed(() => {
    const n = this.noktalar();
    if (n.length < 2) return '';
    const h = this.yukseklik() - this.dolgu;
    const cizgi = n.map(p => `${p.x},${p.y}`).join(' ');
    return `${n[0].x},${h} ${cizgi} ${n[n.length - 1].x},${h}`;
  });

  gridCizgileri = computed(() => {
    const h = this.yukseklik();
    const p = this.dolgu;
    const alan = h - p * 2;
    const sonuc: number[] = [];
    for (let i = 1; i <= this.gridSayisi; i++) {
      sonuc.push(p + (alan * i) / (this.gridSayisi + 1));
    }
    return sonuc;
  });

  hoverNokta = computed(() => {
    const idx = this.hoverIdx();
    const n = this.noktalar();
    return idx >= 0 && idx < n.length ? n[idx] : null;
  });

  hoverBilgi = computed(() => {
    const idx = this.hoverIdx();
    const v = this.veriler();
    const e = this.etiketler();
    if (idx < 0 || idx >= v.length) return null;
    const n = this.noktalar()[idx];
    if (!n) return null;
    const svgW = this.svgRect?.width || 1;
    const gW = this.genislik();
    return {
      deger: v[idx],
      etiket: e?.[idx] || null,
      solPx: (n.x / gW) * svgW,
    };
  });

  fareHareket(e: MouseEvent) {
    const svg = e.currentTarget as SVGSVGElement;
    this.svgRect = svg.getBoundingClientRect();
    const oranX = (e.clientX - this.svgRect.left) / this.svgRect.width;
    const n = this.noktalar();
    if (!n.length) return;
    const gX = oranX * this.genislik();
    let enYakin = 0;
    let enYakinMesafe = Infinity;
    for (let i = 0; i < n.length; i++) {
      const mesafe = Math.abs(n[i].x - gX);
      if (mesafe < enYakinMesafe) {
        enYakinMesafe = mesafe;
        enYakin = i;
      }
    }
    this.hoverIdx.set(enYakin);
  }
}
